package fr.frux.feature.treeadd.wizard

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.selection.selectableGroup
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.CertaintyCard
import fr.frux.core.designsystem.components.DuplicateNearbyBanner
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxSearchField
import fr.frux.core.designsystem.components.SpeciesTile
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.icons.FruxIcons
import fr.frux.feature.treeadd.resources.Res
import fr.frux.feature.treeadd.resources.treeadd_certainty_certain
import fr.frux.feature.treeadd.resources.treeadd_certainty_probable
import fr.frux.feature.treeadd.resources.treeadd_certainty_to_confirm
import fr.frux.feature.treeadd.resources.wizard_duplicate_confirm
import fr.frux.feature.treeadd.resources.wizard_duplicate_subtitle
import fr.frux.feature.treeadd.resources.wizard_duplicate_title
import fr.frux.feature.treeadd.resources.wizard_next
import fr.frux.feature.treeadd.resources.wizard_species_around_heading
import fr.frux.feature.treeadd.resources.wizard_species_certain_description
import fr.frux.feature.treeadd.resources.wizard_species_certainty_title
import fr.frux.feature.treeadd.resources.wizard_species_change
import fr.frux.feature.treeadd.resources.wizard_species_chosen_label
import fr.frux.feature.treeadd.resources.wizard_species_count
import fr.frux.feature.treeadd.resources.wizard_species_count_none
import fr.frux.feature.treeadd.resources.wizard_species_hint
import fr.frux.feature.treeadd.resources.wizard_species_next_hint
import fr.frux.feature.treeadd.resources.wizard_species_no_result_hint
import fr.frux.feature.treeadd.resources.wizard_species_no_result_title
import fr.frux.feature.treeadd.resources.wizard_species_probable_description
import fr.frux.feature.treeadd.resources.wizard_species_results
import fr.frux.feature.treeadd.resources.wizard_species_search_clear
import fr.frux.feature.treeadd.resources.wizard_species_search_placeholder
import fr.frux.feature.treeadd.resources.wizard_species_season_heading
import fr.frux.feature.treeadd.resources.wizard_species_suggest
import fr.frux.feature.treeadd.resources.wizard_species_tile_description
import fr.frux.feature.treeadd.resources.wizard_species_title
import fr.frux.feature.treeadd.resources.wizard_species_to_confirm_description
import fr.frux.feature.treeadd.resources.wizard_species_unavailable_message
import fr.frux.feature.treeadd.resources.wizard_species_unavailable_title
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.Species
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** Tag stable du bouton « Suivant » de l'étape Espèce (E2E). */
internal const val NEXT_TAG = "wizard_next"

private const val GRID_COLUMNS = 3
private val STEP_PADDING = 20.dp
private val STEP_SPACING = 18.dp

/**
 * Étape 2 : choix de l'espèce. Contenu défilant, pied « Suivant » ancré hors du défilement.
 * Les paramètres plats (état, contrôleur) laissent l'écran testable sans l'hôte.
 */
@Composable
internal fun SpeciesStep(
    state: SpeciesStepState,
    controller: SpeciesStepController,
    onNext: () -> Unit,
    onExistingTreeSelected: (Long) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Column(
            modifier =
                Modifier
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(STEP_PADDING),
            verticalArrangement = Arrangement.spacedBy(STEP_SPACING),
        ) {
            Text(stringResource(Res.string.wizard_species_title), style = MaterialTheme.typography.headlineMedium)
            val selected = state.selected
            if (selected == null) {
                PickerContent(state, controller)
            } else {
                ChosenContent(state, selected, controller, onExistingTreeSelected)
            }
        }
        SpeciesFooter(canContinue = state.canContinue, onNext = onNext)
    }
    if (state.suggestion.dialogVisible) {
        SuggestSpeciesDialog(
            initialName = state.query,
            sending = state.suggestion.sending,
            error = state.suggestion.error,
            onDismiss = controller::hideSuggestion,
            onSubmit = { name, comment -> controller.submitSuggestion(name, comment.ifBlank { null }) },
        )
    }
}

@Composable
private fun PickerContent(
    state: SpeciesStepState,
    controller: SpeciesStepController,
) {
    Text(
        text = stringResource(Res.string.wizard_species_hint),
        style = MaterialTheme.typography.bodyLarge,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
    )
    FruxSearchField(
        value = state.query,
        onValueChange = controller::onQueryChange,
        placeholder = stringResource(Res.string.wizard_species_search_placeholder),
        clearLabel = stringResource(Res.string.wizard_species_search_clear),
    )
    if (state.referential == ReferentialLoad.Failed) {
        ErrorState(
            message = stringResource(Res.string.wizard_species_unavailable_message),
            onRetry = controller::retry,
            title = stringResource(Res.string.wizard_species_unavailable_title),
        )
        return
    }
    val grid = state.grid
    when (grid.kind) {
        SpeciesGridKind.NO_RESULT -> {
            NoResultCard(state.query, controller::showSuggestion)
        }

        else -> {
            GridHeading(grid)
            SpeciesGridContent(grid, controller::select)
        }
    }
}

@Composable
private fun GridHeading(grid: SpeciesGrid) {
    val text =
        when (grid.kind) {
            SpeciesGridKind.SEARCH -> {
                pluralStringResource(Res.plurals.wizard_species_results, grid.tiles.size, grid.tiles.size)
            }

            SpeciesGridKind.SEASON_FALLBACK -> {
                stringResource(Res.string.wizard_species_season_heading)
            }

            else -> {
                stringResource(Res.string.wizard_species_around_heading)
            }
        }
    Text(text = text, style = MaterialTheme.typography.labelLarge)
}

@Composable
private fun SpeciesGridContent(
    grid: SpeciesGrid,
    onSelect: (Species) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
        for (rowStart in 0 until grid.tiles.size step GRID_COLUMNS) {
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                for (index in rowStart until rowStart + GRID_COLUMNS) {
                    val cell = Modifier.weight(1f)
                    if (index < grid.tiles.size) {
                        SpeciesGridTile(grid.tiles[index], onSelect, cell)
                    } else {
                        Box(cell)
                    }
                }
            }
        }
    }
}

@Composable
private fun SpeciesGridTile(
    item: SpeciesTileItem,
    onSelect: (Species) -> Unit,
    modifier: Modifier,
) {
    val species = item.species
    val countLabel =
        when (item.count) {
            null -> null
            0 -> stringResource(Res.string.wizard_species_count_none)
            else -> pluralStringResource(Res.plurals.wizard_species_count, item.count, item.count)
        }
    SpeciesTile(
        name = boldMatch(species.commonNameFr, item.match),
        countLabel = countLabel,
        glyph = species.glyph(),
        tint = speciesTint(species.scientificName),
        contentDescription =
            if (countLabel == null) {
                species.commonNameFr
            } else {
                stringResource(Res.string.wizard_species_tile_description, species.commonNameFr, countLabel)
            },
        onClick = { onSelect(species) },
        modifier = modifier,
    )
}

private fun boldMatch(
    text: String,
    match: IntRange?,
): AnnotatedString =
    buildAnnotatedString {
        if (match == null) {
            append(text)
        } else {
            append(text.substring(0, match.first))
            withStyle(SpanStyle(fontWeight = FontWeight.Bold)) { append(text.substring(match.first, match.last + 1)) }
            append(text.substring(match.last + 1))
        }
    }

@Composable
private fun NoResultCard(
    query: String,
    onSuggest: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
    ) {
        Column(modifier = Modifier.padding(20.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(
                text = stringResource(Res.string.wizard_species_no_result_title, query.trim()),
                style = MaterialTheme.typography.titleMedium,
            )
            Text(
                text = stringResource(Res.string.wizard_species_no_result_hint),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            FruxButton(
                text = stringResource(Res.string.wizard_species_suggest),
                onClick = onSuggest,
                style = FruxButtonStyle.Outline,
                icon = FruxIcons.Add,
            )
        }
    }
}

@Composable
private fun ChosenContent(
    state: SpeciesStepState,
    species: Species,
    controller: SpeciesStepController,
    onExistingTreeSelected: (Long) -> Unit,
) {
    ChosenSummary(species, onChange = controller::change)
    Text(stringResource(Res.string.wizard_species_certainty_title), style = MaterialTheme.typography.titleMedium)
    CertaintyGroup(state.certainty, controller::selectCertainty)
    state.duplicate?.let { duplicate ->
        val other = duplicate.tree.species
        DuplicateNearbyBanner(
            glyph = speciesGlyph(other.scientificName, null),
            tint = speciesTint(other.scientificName),
            title = stringResource(Res.string.wizard_duplicate_title, other.commonNameFr, duplicate.roundedMeters),
            subtitle = stringResource(Res.string.wizard_duplicate_subtitle),
            confirmLabel = stringResource(Res.string.wizard_duplicate_confirm),
            onConfirm = { onExistingTreeSelected(duplicate.tree.id) },
        )
    }
}

@Composable
private fun ChosenSummary(
    species: Species,
    onChange: () -> Unit,
) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(20.dp),
        color = MaterialTheme.colorScheme.surfaceVariant,
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            TreeGlyphDisc(glyph = species.glyph(), tint = speciesTint(species.scientificName), size = 56.dp)
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.wizard_species_chosen_label).uppercase(),
                    style = MaterialTheme.typography.labelMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    text = species.commonNameFr,
                    style = MaterialTheme.typography.titleLarge,
                    color = MaterialTheme.colorScheme.onSurface,
                )
                Text(
                    text = species.scientificName,
                    style = MaterialTheme.typography.bodyMedium,
                    fontStyle = FontStyle.Italic,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            FruxButton(
                text = stringResource(Res.string.wizard_species_change),
                onClick = onChange,
                style = FruxButtonStyle.Text,
            )
        }
    }
}

@Composable
private fun CertaintyGroup(
    selected: Certainty,
    onSelect: (Certainty) -> Unit,
) {
    Column(modifier = Modifier.selectableGroup(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
        CertaintyCard(
            title = stringResource(Res.string.treeadd_certainty_certain),
            description = stringResource(Res.string.wizard_species_certain_description),
            bars = 3,
            selected = selected == Certainty.CERTAIN,
            onSelect = { onSelect(Certainty.CERTAIN) },
        )
        CertaintyCard(
            title = stringResource(Res.string.treeadd_certainty_probable),
            description = stringResource(Res.string.wizard_species_probable_description),
            bars = 2,
            selected = selected == Certainty.PROBABLE,
            onSelect = { onSelect(Certainty.PROBABLE) },
        )
        CertaintyCard(
            title = stringResource(Res.string.treeadd_certainty_to_confirm),
            description = stringResource(Res.string.wizard_species_to_confirm_description),
            bars = 1,
            selected = selected == Certainty.TO_CONFIRM,
            onSelect = { onSelect(Certainty.TO_CONFIRM) },
        )
    }
}

@Composable
private fun SpeciesFooter(
    canContinue: Boolean,
    onNext: () -> Unit,
) {
    val label = stringResource(Res.string.wizard_next)
    Column(modifier = Modifier.fillMaxWidth()) {
        HorizontalDivider(thickness = 1.dp, color = MaterialTheme.colorScheme.outlineVariant)
        Column(
            modifier =
                Modifier.navigationBarsPadding().padding(
                    start = 20.dp,
                    top = 12.dp,
                    end = 20.dp,
                    bottom = 28.dp,
                ),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            FruxButton(
                text = label,
                onClick = onNext,
                enabled = canContinue,
                icon = FruxIcons.ArrowForward,
                modifier = Modifier.fillMaxWidth().testTag(NEXT_TAG).semantics { contentDescription = label },
            )
            if (!canContinue) {
                Text(
                    text = stringResource(Res.string.wizard_species_next_hint),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}
